/** Labels for date-keyed chart buckets (`YYYY-MM-DD` or `YYYY-MM`), formatted in UTC so the bucket's day never shifts. */

const dayFull = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});
const dayShort = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
const monthFull = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const monthShort = new Intl.DateTimeFormat('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' });

function parse(key: string): { date: Date; kind: 'day' | 'month' } | null {
  if (/^\d{4}-\d{2}-\d{2}/.test(key)) {
    const d = new Date(`${key.slice(0, 10)}T00:00:00Z`);
    return Number.isNaN(d.getTime()) ? null : { date: d, kind: 'day' };
  }
  if (/^\d{4}-\d{2}$/.test(key)) {
    const d = new Date(`${key}-01T00:00:00Z`);
    return Number.isNaN(d.getTime()) ? null : { date: d, kind: 'month' };
  }
  return null;
}

/** Full label, such as "Sep 15, 2026" or "September 2026". Falls back to the key. */
export function periodLabel(key: string): string {
  const p = parse(key);
  if (!p) return key;
  return p.kind === 'day' ? dayFull.format(p.date) : monthFull.format(p.date);
}

/** Short axis label, such as "Sep 15" or "Sep 26". */
export function periodAxisLabel(key: string): string {
  const p = parse(key);
  if (!p) return key;
  return p.kind === 'day' ? dayShort.format(p.date) : monthShort.format(p.date);
}

/** Keeps the axis quiet: with many points, only every nth gets a label (first and last always do). */
export function axisLabelAt(index: number, total: number, maxLabels = 8): boolean {
  if (total <= maxLabels) return true;
  const step = Math.ceil(total / maxLabels);
  return index % step === 0 || index === total - 1;
}

const DAY_MS = 86_400_000;
const isoDay = (d: Date) => d.toISOString().slice(0, 10);
const isoMonth = (d: Date) => d.toISOString().slice(0, 7);

/**
 * Every period key in the last `days` days ending on `today` (UTC), so a chart can hold a 0 for days without
 * spend instead of drawing a ramp between the two days that had data. Monthly keys cover the same span.
 */
export function fillPeriods(granularity: 'day' | 'month', days: number, today: Date): string[] {
  const span = Math.max(1, Math.floor(days));
  const end = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  const start = end - (span - 1) * DAY_MS;
  const keys: string[] = [];
  if (granularity === 'day') {
    for (let t = start; t <= end; t += DAY_MS) keys.push(isoDay(new Date(t)));
    return keys;
  }
  const first = new Date(start);
  const cursor = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), 1));
  const last = isoMonth(new Date(end));
  while (isoMonth(cursor) <= last) {
    keys.push(isoMonth(cursor));
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return keys;
}
